/**
 * Step one — `clients_create!`. The organisation, the client row and its first owner.
 *
 * One action and one form, drawn by `ActionForm` from the published input schema, so
 * a field the tool gains is a field this step gains and nobody has to remember. What
 * this file adds is the **sentence beside it**: the tier and the currency are decided
 * here and are not ordinary settings afterwards, and a studio member typing them for
 * the first time is the one person who needs to be told.
 *
 * `clients_create` is `!` rather than `$` — it commits nothing — so there is no
 * confirmation, and the answer is the client row itself.
 */

import { ClientSchema } from '@fareast/shared'

import { Prose } from '../../../components/portal/Text.js'
import { ActionForm } from '../../../components/studio/ActionForm.js'

export type DetailsStepProps = {
  /** Given the new client's slug once the account exists. */
  readonly onCreated: (slug: string) => void
}

export function DetailsStep({ onCreated }: DetailsStepProps) {
  return (
    <ActionForm
      action="clients_create"
      onDone={(output) => {
        onCreated(ClientSchema.parse(output).slug)
      }}
      submitLabel="Open the account"
    >
      {/*
        The tool's name used to be in this sentence — "it moves afterwards only through
        `clients_set_tier`, which is audited" — which tells a studio member the one
        thing they cannot use and none of the two things they can. What matters to the
        person on this screen is that changing it later is a deliberate act on the
        client's own page and that it is on the record; the tool is how an agent would
        do the same thing, and the tool list is not this form's business.
      */}
      <Prose>
        The <b>market tier</b> and the <b>billing currency</b> are set here, once. The tier follows
        the market the work targets rather than where the company is incorporated, and it can be
        changed afterwards only from the client’s own page, where the change goes on the record. The
        currency does not move at all: every invoice, every price and every allowance on this
        account is in it.
      </Prose>
      <Prose>
        The first owner is created with the account and is <b>not emailed</b>. Sending them a link
        is the next step but one.
      </Prose>
    </ActionForm>
  )
}
